import { AfterViewInit, Component, Input, NgZone, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { FundPerformance } from 'src/app/domain/fund-performance/dto/FundPerformance';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { CheckBoxSelector } from 'src/app/domain/shared/component/checkbox-selector/checkbox-selector.component';
import { CHART_COLORS, CHART_FONTS, CHART_STYLES }
  from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';
/* eslint-disable */

@Component({
  selector: 'app-net-fund-performance-chart',
  templateUrl: './net-fund-performance-chart.component.html',
  styleUrls: ['./net-fund-performance-chart.component.scss'],
})
export class NetFundPerformanceChartComponent implements OnChanges, AfterViewInit {
  @Input() fundPerformances: FundPerformance[] = [];
  @Input() chartPeriods: CheckBoxSelector[];
  @Input() updateTrigger: boolean;
  @Input() showTitle = true;
  @Input() showEmptyState = false;
  @Input() parent: 'fund-performance' | 'investor-report' | 'published-investor-report' = 'fund-performance';

  alreadyRendered = false;

  min = 0;
  max = 0;
  options;

  constructor(private zone: NgZone) {}

  arrayMin(arr: number[]): void {
    let len: number = arr.length;
    while (len--) {
      if (arr[len] < this.min) {
        this.min = arr[len];
      }
    }
  };

  arrayMax(arr): void {
    let len = arr.length;
    while (len--) {
      if (arr[len] > this.max) {
        this.max = arr[len];
      }
    }
  };

  returnOptions(): void {

    this.options = {
      chart: {
        style: {
          fontFamily: 'Inter',
        },
        events: CHART_STYLES.eventForScrollingWithManualRedraw,
      },
      customRange: 19,
      title: undefined,
      xAxis: [{
        labels: {
          style: { fontSize: CHART_FONTS.font4, color: '#78909C' },
          format: '{value.period}<br><span style="font-weight: 500; color: #00948E">{value.isPredicted}</span>',
        },
        categories: [],
        type: 'category',
        crosshair: true,
        scrollbar: CHART_STYLES.scrollbar,
      }],
      yAxis: [{ // Primary yAxis
        tickPositions: [],
        title: {
          text: undefined,
        },
        floor: -2,
        labels: {
          format: '{value}M',
          style: { fontSize: CHART_FONTS.font4, color: CHART_COLORS.black12 },
        },
        stackLabels: {
          enabled: true,
          allowOverlap: false,
          y: -5,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          // eslint-disable-next-line
          formatter: function () {
            if (this.total !== 0) {
              const decimals = this.total > 100 ? 0 : 1;
              return Highcharts.numberFormat(this.total, decimals);
            }
          },
        },
      },
      { // Secondary yAxis
        title: {
          text: TVPI,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font3 },
        },
        min: 0,
        tickAmount: 9,
        labels: {
          format: '{value}',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
        },
        opposite: true,
      }],
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        backgroundColor: 'white',
        borderRadius: 12,
        shadow: false,
        useHTML: true,
        headerFormat: '<span style="font-size: 0.8rem"><b><span style="color: #00948E">{point.key.isPredicted}</span>\
        {point.key.period}</b></span><br><br>',
        style: { fontSize: CHART_FONTS.font4 },
        valueSuffix: ' M',
        pointFormatter(): string {
          const colorDot = '<span style="color: ' + this.color + '">&#9679;</span>';
          if (this.y) {
            const prefix = (this.series.name === 'TVPI' || this.series.name === 'DPI') ? `x${this.y.toFixed(2)}` : `${this.y.toFixed(2)}M`;
            const suffix = '<br>';
            return `${colorDot} ${this.series.name}: <b>${prefix}</b>${suffix}`;
          }
          return `${colorDot} ${this.series.name}: <b>-</b><br>`;
        },
      },
      legend: {
        itemStyle: { fontSize: CHART_FONTS.font4 },
        align: 'left',
        verticalAlign: 'top',
        floating: false,
      },
      plotOptions: {
        series: {
          pointWidth: 100,
          events: {
            // eslint-disable-next-line
            legendItemClick: function (event) { }
          },
        },
        column: {
          width: 150,
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            inside: true,
            color: 'white',
            rotation: 270,
            style: {
              textShadow: false,
              textOutline: 'none',
            },
          },
        },

      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: 'Distribution',
          type: 'column',
          yAxis: 0,
          stack: 'partCap&Dist',
          data: [],
          color: '#FFDD2D',
          tooltip: {
            pointFormat: '{series.name}: <b>{point.y:.1f}M</b><br>',
            valueSuffix: 'M',
          },
          dataLabels: {
            enabled: true,
            color: CHART_COLORS.black12,
          },
        },
        {
          name: 'Net asset value',
          type: 'column',
          yAxis: 0,
          stack: 'partCap&Dist',
          data: [],
          color: '#B2B2B4',
          tooltip: {
            pointFormat: '{series.name}: <b>{point.y:.1f}M</b><br>',
            valueSuffix: 'M',
          },
        },
        {
          name: 'Net gain/(loss)',
          type: 'column',
          yAxis: 0,
          stack: 'Cont & Gain',
          data: [],
          color: '#5CC3BB',
          tooltip: {
            pointFormat: '{series.name}: <b>{point.y:.1f}M</b><br>',
            valueSuffix: 'M',
          },
        },
        {
          name: 'Contribution',
          type: 'column',
          yAxis: 0,
          stack: 'Cont & Gain',
          data: [],
          color: '#5E6F78',
          tooltip: {
            pointFormat: '{series.name}: <b>{point.y:.1f}M</b><br>',
            valueSuffix: 'M',
          },
        },
        {
          name: TVPI,
          type: 'spline',
          yAxis: 1,
          data: [],
          color: '#5E6F78',
          tooltip: {
            pointFormat: '{series.name}: <b>x{point.y:.1f}</b><br>',
            valueSuffix: '',
          },
          marker: {
            enabled: false,
            lineWidth: 3,
            fillColor: 'white',
          },
        },
        {
          name: 'DPI',
          type: 'spline',
          yAxis: 1,
          data: [],
          visible: false,
          tooltip: {
            pointFormat: '{series.name}: <b>x{point.y:.1f}</b><br>',
            valueSuffix: '',
          },
          marker: {
            enabled: false,
            lineWidth: 3,
            fillColor: 'white',
          },
        },
      ],
      exporting: { enabled: false },
      colors: ['#F2C816'/* yellow*/, '#00B8AA'/* teal*/, '#8BD4EA'/* skyblue*/, '#37464A'/* charcoal*/, '#5F6C6E',
        '#F16360', '#F79568', '#FFF263', '#A76A9A', '#DEBFBF', '#DEBFBF'],
      lang: {
        thousandsSep: ',',
        decimalPoint: '.',
        noData: 'No data available',
      },
      responsive: CHART_STYLES.responsiveLegendTwoYAxis,
    };
  }

  ngAfterViewInit(): void {
    if (this.showEmptyState) return;
    this.render();
    this.alreadyRendered = true;
  }

  nullToZero(val: number): number {
    if (val == null) {
      return 0;
    }
    return val;
  }

  ngOnChanges(): void {
    if (this.fundPerformances?.length) {
      const periods: string[] = [];
      const nav: number[] = [];
      const dist: number[] = [];
      const contribution: number[] = [];
      const netGainLoss: number[] = [];
      const tvpi: number[] = [];
      const dpi: number[] = [];
      const predicted: string[] = [];
      const selectedPeriods = this.chartPeriods.filter((c) => c.chosen === true).map((c) => c.item);

      this.fundPerformances.sort((f1, f2) => new Date(f1.period).getTime() - new Date(f2.period).getTime());
      this.fundPerformances.forEach((el) => {
        if (selectedPeriods.includes(el.period.toString())) {
          periods.push(el.period.toString());
          const totalPositiveDistribution = -el.distribution - el.carryDistribution;
          const navOfEl = (el.totalExpenses || el.totalExpenses === 0) ?
            (this.nullToZero(el.contribution) - this.nullToZero(totalPositiveDistribution) -
            Math.abs(this.nullToZero(el.totalExpenses)) + el.totalFmv - el.totalCost) : 0;

          if (navOfEl && el.contribution && (el.totalExpenses || el.totalExpenses === 0)) {
            netGainLoss.push(Number(((totalPositiveDistribution + navOfEl - el.contribution) / 1000000).toFixed(2)));
            nav.push(Number((navOfEl / 1000000).toFixed(2)));
          } else {
            netGainLoss.push(null);
            nav.push(null);
          }

          if (el.totalExpenses || el.totalExpenses === 0) {
            dist.push(Number((totalPositiveDistribution / 1000000).toFixed(2)));
            contribution.push(Number((el.contribution / 1000000).toFixed(2)));
          } else {
            dist.push(null);
            contribution.push(null);
          }

          if (navOfEl && el.contribution && (el.totalExpenses || el.totalExpenses === 0)) {
            tvpi.push(Number(((navOfEl - this.nullToZero(el.distribution) - this.nullToZero(el.carryDistribution)) / el.contribution).toFixed(2)));
          } else {
            tvpi.push(null);
          }

          if (el.distribution && el.contribution && (el.totalExpenses || el.totalExpenses === 0)) {
            dpi.push(Number((totalPositiveDistribution / el.contribution).toFixed(2)));
          } else {
            dpi.push(null);
          }
          predicted.push(el.isBasedOnPrognosis ? 'Predicted<br>' : undefined);
        }
      });

      this.min = 0;
      this.max = 0;

      this.arrayMin(netGainLoss);
      this.arrayMax(netGainLoss);
      this.arrayMax(nav);
      this.arrayMax(dist);
      this.arrayMax(contribution);

      if (this.min > 0) {
        this.min = 0;
      }
      const interval = Number(((this.max - this.min) / 5).toFixed(0)) + 1;
      const ticks = [];

      let floor = Number((this.min / interval * -1).toFixed(0));
      if (this.min !== 0 && floor === 0) {
        floor++;
      }
      const positives = 8 - floor + 1;
      while (floor > 0) {
        ticks.push(interval * floor * -1);
        floor--;
      }

      for (let i = 0; i < positives; i++) {
        ticks.push(interval * i);
      }

      this.returnOptions();


      const newData = [];
      periods.forEach((value, i) => {
        newData.push({
          period: value,
          isPredicted: predicted[i],
        });
      });


      this.options.xAxis[0].categories = newData;
      this.options.series[0].data = dist;
      this.options.series[1].data = nav;
      this.options.series[2].data = netGainLoss;
      this.options.series[3].data = contribution;
      this.options.series[4].data = tvpi;
      this.options.series[5].data = dpi;
      this.options.yAxis[0].tickPositions = ticks;

      this.options.plotOptions = {
        column: {
          width: 100,
          stacking: 'normal',
          pointPadding: 0.1,
          groupPadding: 0.1,
          dataLabels: {
            enabled: true,
            inside: true,
            color: 'white',
            rotation: 270,
            style: {
              textShadow: false,
              textOutline: 'none',
            },
            // eslint-disable-next-line
            formatter: function () {
              if (this.point.shapeArgs.height >= 60) {
                const decimals = this.y > 100 ? 0 : 1;
                return Highcharts.numberFormat(this.y, decimals);
              }
              this.point.visible = false; // <--hide this dataLabel
              return ''; // <--have to return "something" so, return an empty string
            },
          },
        },
      };

      if (newData.length < 10) {
        this.options.yAxis[0].stackLabels.style.fontSize = CHART_FONTS.font4;
        this.options.plotOptions.column.dataLabels.style.fontSize = CHART_FONTS.font4;
      } else if (newData.length < 15) {
        this.options.yAxis[0].stackLabels.style.fontSize = '0.625rem';
        this.options.plotOptions.column.dataLabels.style.fontSize = '0.625rem';
      } else if (newData.length >= 15) {
        this.options.yAxis[0].stackLabels.style.fontSize = '0.5rem';
        this.options.plotOptions.column.dataLabels.style.fontSize = '0.5rem';
      }

      NoDataToDisplay(Highcharts);
      if (this.alreadyRendered) {
        this.render();
      }
    }
  }

  private render(): void {
    this.zone.runOutsideAngular(() => {
      Highcharts.chart('netFundPerf-' + this.parent, this.options);
    });
  }
}

const TVPI = 'TVPI';
